import { User, PDFDocument } from '../types';
import { getToken, setToken, clearToken } from './auth';

const API_BASE = import.meta.env.PROD
  ? 'https://qr-pdf-worker.namanshishangiya-d66.workers.dev/api'
  : '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...headers, ...(options.headers as Record<string, string>) },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error((error as { message?: string }).message || `HTTP ${response.status}`);
  }

  return response.json();
}

// Auth

export async function register(
  email: string,
  password: string,
  name: string,
): Promise<User> {
  const data = await request<{ token: string; user: User }>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name }),
  });
  setToken(data.token);
  return data.user;
}

export async function login(email: string, password: string): Promise<User> {
  const data = await request<{ token: string; user: User }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(data.token);
  return data.user;
}

export async function getMe(): Promise<User> {
  const data = await request<{ user: User }>('/auth/me');
  return data.user;
}

export function logout(): void {
  clearToken();
}

// Documents

interface DocumentRow {
  id: string;
  title: string;
  file_name: string;
  file_size: number;
  slug: string;
  summary: string | null;
  owner_id: string;
  r2_key: string;
  upload_date: string;
  last_updated: string;
}

function rowToDoc(row: DocumentRow): PDFDocument {
  return {
    id: row.id,
    title: row.title,
    fileName: row.file_name,
    fileSize: row.file_size,
    slug: row.slug,
    summary: row.summary ?? undefined,
    ownerId: row.owner_id,
    r2Key: row.r2_key,
    uploadDate: row.upload_date,
    lastUpdated: row.last_updated,
  };
}

export async function listDocuments(): Promise<PDFDocument[]> {
  const data = await request<{ documents: DocumentRow[] }>('/documents');
  return data.documents.map(rowToDoc);
}

export async function createDocument(
  title: string,
  fileName: string,
  fileSize: number,
): Promise<{ document: PDFDocument; uploadUrl: string }> {
  const data = await request<{ document: DocumentRow; uploadUrl: string }>('/documents', {
    method: 'POST',
    body: JSON.stringify({ title, fileName, fileSize }),
  });
  return { document: rowToDoc(data.document), uploadUrl: data.uploadUrl };
}

export async function updateDocument(
  id: string,
  opts: { title?: string; fileName?: string; fileSize?: number; newFile?: boolean },
): Promise<{ document: PDFDocument; uploadUrl?: string }> {
  const data = await request<{ document: DocumentRow; uploadUrl?: string }>(
    `/documents/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(opts),
    },
  );
  return { document: rowToDoc(data.document), uploadUrl: data.uploadUrl };
}

export async function deleteDocument(id: string): Promise<void> {
  await request(`/documents/${id}`, { method: 'DELETE' });
}

export async function confirmUpload(id: string): Promise<PDFDocument> {
  const data = await request<{ document: DocumentRow }>(`/documents/${id}/confirm-upload`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return rowToDoc(data.document);
}

// Public

export async function getPublicDocument(
  slug: string,
): Promise<{ document: PDFDocument; downloadUrl: string }> {
  const data = await request<{
    document: {
      id: string;
      title: string;
      fileName: string;
      fileSize: number;
      slug: string;
      summary: string | null;
      uploadDate: string;
      lastUpdated: string;
    };
    downloadUrl: string;
  }>(`/public/v/${slug}`);

  return {
    document: {
      id: data.document.id,
      title: data.document.title,
      fileName: data.document.fileName,
      fileSize: data.document.fileSize,
      slug: data.document.slug,
      summary: data.document.summary ?? undefined,
      ownerId: '',
      uploadDate: data.document.uploadDate,
      lastUpdated: data.document.lastUpdated,
    },
    downloadUrl: data.downloadUrl,
  };
}
